import { BaseAnalyticsClient } from './base';
import type { UserTraits } from '../types/user';
import type {
  AnalyticsContext,
  ApiValue,
  GroupTraits
} from '../types/properties';
import type {
  RudderAnalytics,
  ApiObject,
  IdentifyTraits
} from '@rudderstack/analytics-js';

export class BrowserAnalyticsClient extends BaseAnalyticsClient {
  private rudderanalytics?: RudderAnalytics;
  private initializePromise?: Promise<void>;

  // ============================================================================
  // Base Implementation - Required by BaseAnalyticsClient
  // ============================================================================

  initialize(): Promise<void> {
    if (this.initializePromise) return this.initializePromise;
    this.initializePromise = this._doInitialize();
    return this.initializePromise;
  }

  private async _doInitialize(): Promise<void> {
    if (typeof window === 'undefined') {
      this.warn('Browser client requires window object');
      return;
    }

    try {
      const { RudderAnalytics: RudderAnalyticsClass } = await import(
        '@rudderstack/analytics-js'
      );
      const rudderanalytics = new RudderAnalyticsClass();

      // Wait for the SDK to be ready so getAnonymousId() is reliable.
      // A 5-second timeout ensures initialize() always resolves even if the
      // SDK never fires ready (e.g. invalid write key, blocked integrations).
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, 5000);
        rudderanalytics.ready(() => {
          clearTimeout(timeout);
          resolve();
        });
        rudderanalytics.load(this.config.writeKey, this.config.dataPlaneUrl, {
          logLevel: this.config.debug ? 'DEBUG' : 'ERROR',
          integrations: { All: true },
          loadIntegration: true
        });
      });

      this.rudderanalytics = rudderanalytics;

      if (this.config.debug) {
        this.log('Browser client initialized');
      }
    } catch (error) {
      this.error(`Failed to initialize browser client: ${error}`);
    }
  }

  protected _track(
    eventName: string,
    properties: Record<string, ApiValue>,
    context?: AnalyticsContext,
    _userId?: string,
    _anonymousId?: string
  ): void {
    if (!this.rudderanalytics) {
      this.warn('Browser client not initialized');
      return;
    }

    try {
      // Browser SDK handles userId and anonymousId through identify() call automatically
      // _userId and _anonymousId are unused here but required by the base class signature
      this.rudderanalytics.track(
        eventName,
        properties as unknown as ApiObject,
        context ? { context: context as unknown as ApiObject } : undefined
      );
    } catch (error) {
      this.error(`Track error: ${error}`);
    }
  }

  protected _identify(
    userId: string,
    traits?: UserTraits,
    context?: AnalyticsContext,
    anonymousId?: string
  ): void {
    if (!this.rudderanalytics) {
      this.warn('Browser client not initialized');
      return;
    }

    try {
      // Browser SDK signature: identify(userId, traits, options, callback)
      this.rudderanalytics.identify(
        userId,
        traits as unknown as IdentifyTraits,
        {
          context: context as unknown as ApiObject,
          anonymousId
        }
      );
    } catch (error) {
      this.error(`Identify error: ${error}`);
    }
  }

  protected _group(
    groupId: string,
    traits?: GroupTraits,
    context?: AnalyticsContext,
    _userId?: string,
    _anonymousId?: string
  ): void {
    if (!this.rudderanalytics) {
      this.warn('Browser client not initialized');
      return;
    }

    try {
      // Browser SDK signature: group(groupId, traits, options, callback)
      // Browser SDK handles userId automatically through identify() call
      // _userId and _anonymousId are unused here but required by the base class signature
      this.rudderanalytics.group(
        groupId,
        traits as unknown as IdentifyTraits,
        context ? { context: context as unknown as ApiObject } : undefined
      );
    } catch (error) {
      this.error(`Group error: ${error}`);
    }
  }

  protected _anonymize(): void {
    if (!this.rudderanalytics) return;
    try {
      // Empty userId signals RudderStack to disassociate the current user.
      // Subsequent events are attributed to anonymousId only.
      this.rudderanalytics.identify('');
    } catch (error) {
      this.error(`Anonymize error: ${error}`);
    }
  }

  getAnonymousId(): string | undefined {
    return this.rudderanalytics?.getAnonymousId() ?? super.getAnonymousId();
  }

  reset(): void {
    if (!this.rudderanalytics) return;

    try {
      this.rudderanalytics.reset();
      this.clearUserContext();
      this.clearGroupContext();
    } catch (error) {
      this.error(`Reset error: ${error}`);
    }
  }

  // ============================================================================
  // Browser Client Specific Methods
  // ============================================================================

  /**
   * Track a generic page view (used by automatic tracking)
   */
  pageView(
    name: string,
    properties?: {
      path?: string;
      url?: string;
      title?: string;
      referrer?: string;
      category?: string;
      [key: string]: ApiValue;
    }
  ): void {
    if (!this.rudderanalytics || !this.config.enabled) return;

    try {
      const { category, ...otherProps } = properties || {};
      if (category) {
        this.rudderanalytics.page(
          category,
          name,
          otherProps as unknown as ApiObject
        );
      } else {
        this.rudderanalytics.page(name, otherProps as unknown as ApiObject);
      }
    } catch (error) {
      this.error(`Page view error: ${error}`);
    }
  }

  /**
   * Track an enriched page view with additional context using track event
   * (e.g., organization ID, event filters, etc.)
   */
  enrichedPageView(
    eventName: string,
    properties: {
      path?: string;
      url?: string;
      title?: string;
      organizationId?: string;
      eventId?: string;
      filters?: Record<string, ApiValue>;
      [key: string]: ApiValue;
    }
  ): void {
    if (!this.rudderanalytics || !this.config.enabled) return;

    try {
      // Use track() for enriched context instead of page()
      this.rudderanalytics.track(eventName, properties as unknown as ApiObject);
    } catch (error) {
      this.error(`Enriched page view error: ${error}`);
    }
  }
}
